import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMatch, useLocation } from 'react-router-dom';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { isEmbedReadyMessage, postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { LogoConfig } from 'src/components/context/AppContextModels';
import { ROUTE_CONFIG } from 'src/utilities/RouteConfig';
import { buildEmbeddedAppPath } from 'src/utilities/urlName';

export interface EmbeddedAppInfo {
  url: string | null;
  name: string | null;
  logoConfig: LogoConfig | null;
  productType: string | null;
}

export interface EmbeddedAppState {
  embeddedApp: EmbeddedAppInfo | null;
  embedMatch: ReturnType<typeof useMatch> | null;
  sendMessageToEmbed: (type: string, payload?: Record<string, unknown>) => boolean;
  displayAppName: string | null;
  routeConfig: (typeof ROUTE_CONFIG)[string] | undefined;
  // Stable identity of the active embedded app (its id), and the canonical
  // in-portal path to it (`/{urlName}` or `/all-apps/{id}`). Optional so
  // consumers/tests that predate the urlName routing still typecheck; when
  // absent, callers fall back to the legacy embedMatch-based path.
  embedKey?: string | null;
  embedPath?: string | null;
}

/** Sends a postMessage to the embedded iframe if it supports the protocol. */
const useSendMessageToEmbed = (embedReadyRef: React.MutableRefObject<boolean>) => {
  return useCallback(
    (type: string, payload?: Record<string, unknown>): boolean => {
      if (!embedReadyRef.current) return false;
      return postMessageToEmbeddedIframe(type, payload);
    },
    [embedReadyRef]
  );
};

export const useEmbeddedApp = (): EmbeddedAppState => {
  const { parsedApplications } = useAppContext();
  const { isDarkMode } = useTheme();
  // Embedded apps live at `/app/:urlName`; `/all-apps/:id` is the legacy shape
  // (redirected, and the permanent fallback for apps without a slug). Match both
  // and normalize to a single key.
  const rootMatch = useMatch('/app/:urlName');
  const legacyMatch = useMatch('/all-apps/:id');
  const matchedKey = legacyMatch?.params?.id ?? rootMatch?.params?.urlName ?? null;
  const { pathname } = useLocation();
  const embedReadyRef = useRef(false);

  // Keep a ref in sync with the context value so the message handler
  // always reads the latest theme without a stale closure.
  const isDarkModeRef = useRef(isDarkMode);
  useEffect(() => {
    isDarkModeRef.current = isDarkMode;
  }, [isDarkMode]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isEmbedReadyMessage(event)) {
        embedReadyRef.current = true;
        // Immediately sync the current theme with the newly-ready embedded app
        postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: isDarkModeRef.current });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Propagate theme changes to an already-loaded embedded iframe
  useEffect(() => {
    if (embedReadyRef.current) {
      postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode });
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (!matchedKey) {
      embedReadyRef.current = false;
    }
  }, [matchedKey]);

  // Resolve the active application (prefer urlName, fall back to id). Note that
  // `/:urlName` matches ANY single segment (e.g. /announcements); those resolve
  // to no app and correctly yield a null embeddedApp.
  const resolvedApp = useMemo(() => {
    if (!matchedKey) return null;
    const active = parsedApplications.filter((a) => a.isActive);
    const app = active.find((a) => a.urlName === matchedKey) ?? active.find((a) => a.id === matchedKey);
    if (!app || !app.embeddingEnabled) return null;
    return app;
  }, [matchedKey, parsedApplications]);

  const embeddedApp = useMemo((): EmbeddedAppInfo | null => {
    if (!resolvedApp) return null;
    return {
      url: resolvedApp.applicationLink || null,
      name: resolvedApp.applicationName || null,
      logoConfig: resolvedApp.logoConfig || null,
      productType: resolvedApp.toolUI || null
    };
  }, [resolvedApp]);

  const embedKey = resolvedApp?.id ?? null;
  const embedPath = resolvedApp ? buildEmbeddedAppPath(resolvedApp) : null;

  const sendMessageToEmbed = useSendMessageToEmbed(embedReadyRef);

  const routeConfig = ROUTE_CONFIG[pathname];
  // Sections whose routes get the embedded-style top-nav treatment (logo
  // swap + arrow + section name beside the FinSuite icon). For kingpin
  // we pin the name to "frame" so all sub-tabs (overview / goals /
  // insights) read the same in the top nav rather than swapping per tab.
  let sectionDisplayName: string | null = null;
  if (routeConfig?.navigationSection === 'org-tools') {
    sectionDisplayName = routeConfig.navigationText?.toLowerCase() ?? null;
  } else if (routeConfig?.navigationSection === 'kingpin') {
    sectionDisplayName = 'frame';
  }
  const displayAppName = (embeddedApp?.productType === 'Prophecy' ? 'prophecy' : embeddedApp?.name?.toLowerCase()) ?? sectionDisplayName ?? null;

  return { embeddedApp, embedMatch: rootMatch ?? legacyMatch, sendMessageToEmbed, displayAppName, routeConfig, embedKey, embedPath };
};
